// pages/grant-review.ts — staff per-grant review screen.
//
// Mirrors frontend2/Admin.jsx :: GrantReviewPage. Action surface:
//   • Start review · Approve · Deny · Request more info
//   • Reassign reviewer (via /api/users/staff)
//   • Toggle IsPublic / IsAmountPublic when approved
//   • Internal vs. applicant-facing messages
//   • Inline PDF preview using api.getDocumentBlob
//
// Role-gated to approver / admin. Approve requires positive amount;
// Deny requires reason ≥ 50 chars; the modals enforce both.

import type { Page, PageContext } from '../router';
import {
  html, esc, fmtMoney, fmtMoneyOrDash, fmtDate, fmtDateTime, fmtFileSize,
  statusPillClass, statusColor,
} from '../util';

interface Grant {
  GrantId: number;
  Status: string;
  ReferenceNumber?: string;
  ProjectName: string;
  ProjectDescription?: string;
  ImpactNarrative?: string;
  OrganizationLegalName?: string;
  TaxId?: string;
  TaxExemptStatus?: string;
  RequestedAmount: number | null;
  ApprovedAmount?: number | null;
  ProjectStartDate?: string;
  ProjectEndDate?: string;
  ContactFirstName?: string;
  ContactLastName?: string;
  ContactEmail?: string;
  ContactPhone?: string;
  AddressStreet?: string;
  AddressCity?: string;
  AddressState?: string;
  AddressZip?: string;
  ReviewedBy?: number | null;
  IsPublic?: boolean;
  IsAmountPublic?: boolean;
  SubmittedAt?: string;
}
interface DocRow {
  DocumentId: number; DocumentType: string; FileName: string; FileSize: number;
  FileExtension?: string; FilePath?: string; UploadedAt?: string;
}
interface Message {
  MessageId?: number; SenderName: string; SenderRole: string;
  SentAt?: string; MessageText: string; IsInternal?: boolean;
}
interface HistoryRow {
  OldStatus: string | null; NewStatus: string;
  ChangedAt?: string; ChangedByName?: string; Notes?: string;
}
interface StaffMember { UserId: number; FullName: string; Role: string; }

type TabId = 'overview' | 'documents' | 'organization' | 'communication';
const TABS: Array<{ id: TabId; label: string }> = [
  { id: 'overview',      label: 'Overview' },
  { id: 'documents',     label: 'Documents' },
  { id: 'organization',  label: 'Organization' },
  { id: 'communication', label: 'Communication' },
];

const DENY_TEMPLATES: Record<string, string> = {
  '501c3':
    `Dear Applicant,\n\nThank you for your interest in funding from The Bray Family Foundation.\n\nUnfortunately, we can only provide grants to organizations with registered 501(c)(3) tax-exempt status. We encourage you to reapply once your organization receives 501(c)(3) designation from the IRS.\n\nSincerely,\nThe Bray Family Foundation Grant Review Team`,
  'incomplete':
    `Dear Applicant,\n\nThank you for submitting your grant application. After review, we found that the application is missing required documentation or contains incomplete information.\n\nWe encourage you to reapply with a complete application.\n\nSincerely,\nThe Bray Family Foundation Grant Review Team`,
  'priorities':
    `Dear Applicant,\n\nThank you for your application. Unfortunately, the proposed project falls outside our current funding priorities.\n\nWe encourage you to review our Grant Center for eligibility criteria and consider reapplying in a future cycle.\n\nSincerely,\nThe Bray Family Foundation Grant Review Team`,
};

function daysBetween(dateVal: string | undefined): number {
  if (!dateVal) return 0;
  const d = new Date(dateVal);
  if (!Number.isFinite(d.getTime())) return 0;
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
}

function roleBadge(role: string | undefined | null): string {
  const r = role || 'User';
  let bg = 'var(--paper-2)', fg = 'var(--ink-soft)';
  if (r === 'Admin')    { bg = 'var(--accent-soft)';  fg = 'var(--accent)'; }
  if (r === 'Approver') { bg = 'var(--primary-soft)'; fg = 'var(--st-approved)'; }
  return `<span style="padding: 2px 8px; border-radius: 999px; font-size: 11.5px; font-weight: 600;
    letter-spacing: 0.04em; text-transform: uppercase; background: ${bg}; color: ${fg};">${esc(r)}</span>`;
}

function renderTimeline(history: HistoryRow[]): string {
  if (!history || history.length === 0) {
    return '<p class="t-small" style="margin: 0;">No history yet.</p>';
  }
  const rows = history.map((h, i) => {
    const last = i === history.length - 1;
    return `
      <li style="display: grid; grid-template-columns: 12px 1fr; gap: 14px;
                 padding: 10px 0; border-bottom: ${last ? 'none' : '1px solid var(--rule)'};">
        <span style="width: 10px; height: 10px; border-radius: 999px;
                     background: ${statusColor(h.NewStatus)}; margin-top: 7px;"></span>
        <div>
          <div style="font-size: 14px; color: var(--ink);">${esc(h.NewStatus)}</div>
          <div class="t-small" style="font-size: 12.5px;">
            ${h.ChangedByName ? esc(h.ChangedByName) + ' · ' : ''}${esc(fmtDate(h.ChangedAt))}
          </div>
          ${h.Notes ? `<p class="t-body" style="margin: 6px 0 0; font-size: 13.5px; white-space: pre-wrap;">${esc(h.Notes)}</p>` : ''}
        </div>
      </li>
    `;
  }).join('');
  return `<ol style="list-style: none; margin: 0; padding: 0;">${rows}</ol>`;
}

// ─── Modal scaffolds ───────────────────────────────────────────────

function approveModal(): string {
  return html`
    <div class="modal fade" id="grApproveModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered" style="max-width: 500px;">
        <div class="modal-content">
          <div class="modal-header" style="border-bottom: 1px solid var(--rule);">
            <h3 class="t-h4 serif" style="margin: 0; font-size: 20px;">Approve this grant?</h3>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <div class="gf-field">
              <label class="form-label" for="gr-approve-amount">Approved amount</label>
              <input type="number" class="form-control" id="gr-approve-amount"/>
              <div class="form-text">May differ from requested amount.</div>
            </div>
            <div id="gr-approve-error" class="gf-field-error" style="display: none;"></div>
            <label style="display: flex; gap: 10px; align-items: center; cursor: pointer; margin-top: 12px;">
              <input type="checkbox" id="gr-approve-public" style="accent-color: var(--primary);"/>
              <span style="font-size: 14px;">Add this grant to the public showcase</span>
            </label>
          </div>
          <div class="modal-footer" style="border-top: 1px solid var(--rule);">
            <button type="button" class="btn btn-outline-primary" data-bs-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-primary" data-action="confirm-approve">Confirm approval</button>
          </div>
        </div>
      </div>
    </div>
  `;
}

function denyModal(): string {
  const opts = `
    <option value="">Custom reason…</option>
    <option value="501c3">Not a 501(c)(3) organization</option>
    <option value="incomplete">Incomplete application</option>
    <option value="priorities">Outside funding priorities</option>
  `;
  return html`
    <div class="modal fade" id="grDenyModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered" style="max-width: 520px;">
        <div class="modal-content">
          <div class="modal-header" style="border-bottom: 1px solid var(--rule);">
            <h3 class="t-h4 serif" style="margin: 0; font-size: 20px;">Deny this grant?</h3>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <div class="gf-field">
              <label class="form-label" for="gr-deny-template">Use a template (optional)</label>
              <select class="form-select" id="gr-deny-template">${{ __raw: opts }}</select>
            </div>
            <div class="gf-field">
              <label class="form-label" for="gr-deny-reason">Denial reason<span style="color: var(--st-denied); margin-left: 2px;">*</span></label>
              <textarea class="form-control" id="gr-deny-reason" rows="6"
                        placeholder="Explain why this application is being denied…"
                        style="resize: vertical;"></textarea>
              <div class="form-text">Min. 50 characters — this will be sent to the applicant.</div>
            </div>
            <p id="gr-deny-counter" style="font-size: 12px; text-align: right; margin: -8px 0 8px;
                                            color: var(--st-denied);">0 / 50 characters minimum</p>
            <div id="gr-deny-error" class="gf-field-error" style="display: none;"></div>
            <label style="display: flex; gap: 10px; align-items: center; cursor: pointer;">
              <input type="checkbox" id="gr-deny-notify" checked style="accent-color: var(--primary);"/>
              <span style="font-size: 14px;">Send denial email to applicant</span>
            </label>
          </div>
          <div class="modal-footer" style="border-top: 1px solid var(--rule);">
            <button type="button" class="btn btn-outline-primary" data-bs-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-danger" data-action="confirm-deny" disabled>Confirm denial</button>
          </div>
        </div>
      </div>
    </div>
  `;
}

function infoModal(): string {
  return html`
    <div class="modal fade" id="grInfoModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered" style="max-width: 480px;">
        <div class="modal-content">
          <div class="modal-header" style="border-bottom: 1px solid var(--rule);">
            <h3 class="t-h4 serif" style="margin: 0; font-size: 20px;">Request more information</h3>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <p class="t-body" style="margin-bottom: 14px;">
              This will change the grant status to <strong>Action Required</strong> and notify
              the applicant to respond.
            </p>
            <div class="gf-field">
              <label class="form-label" for="gr-info-msg">What do you need from the applicant?<span style="color: var(--st-denied); margin-left: 2px;">*</span></label>
              <textarea class="form-control" id="gr-info-msg" rows="4"
                        placeholder="Please provide more details about…"
                        style="resize: vertical;"></textarea>
            </div>
          </div>
          <div class="modal-footer" style="border-top: 1px solid var(--rule);">
            <button type="button" class="btn btn-outline-primary" data-bs-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-primary" data-action="confirm-info" disabled>Send request</button>
          </div>
        </div>
      </div>
    </div>
  `;
}

function deleteModal(): string {
  return html`
    <div class="modal fade" id="grDeleteModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered" style="max-width: 500px;">
        <div class="modal-content">
          <div class="modal-header" style="border-bottom: 1px solid var(--rule);">
            <h3 class="t-h4 serif" style="margin: 0; font-size: 20px;">Delete this draft?</h3>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <p class="t-body" style="margin: 0 0 12px;">
              This permanently removes the draft and all of its uploaded documents,
              messages, and history. The applicant will no longer see it.
            </p>
            <p class="t-body" style="margin: 0; color: var(--st-denied); font-weight: 500;">
              This cannot be undone.
            </p>
            <div id="gr-delete-error" class="gf-field-error" style="display: none; margin-top: 12px;"></div>
          </div>
          <div class="modal-footer" style="border-top: 1px solid var(--rule);">
            <button type="button" class="btn btn-outline-primary" data-bs-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-danger" data-action="confirm-delete">Delete draft</button>
          </div>
        </div>
      </div>
    </div>
  `;
}

function previewModal(): string {
  return html`
    <div class="modal fade" id="grPreviewModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered" style="max-width: 900px;">
        <div class="modal-content">
          <div class="modal-header" style="border-bottom: 1px solid var(--rule);">
            <h3 class="t-h4 serif" style="margin: 0; font-size: 20px;">Document preview</h3>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body" style="padding: 0;">
            <iframe id="gr-preview-frame" title="Document preview"
                    style="width: 100%; height: 70vh; border: none; display: block; background: var(--paper-2);"></iframe>
          </div>
        </div>
      </div>
    </div>
  `;
}

// ─── Page ──────────────────────────────────────────────────────────

const GrantReviewPage: Page = {
  guard(ctx: PageContext): string | null {
    if (!ctx.user) return 'login';
    if (ctx.userRole !== 'admin' && ctx.userRole !== 'approver') return 'landing';
    return null;
  },

  render(ctx: PageContext): string {
    const idRaw = ctx.params.get('id');
    const idNum = idRaw == null ? NaN : Number(idRaw);
    const id: number | null = Number.isFinite(idNum) ? idNum : null;
    if (id == null) {
      return html`
        <main class="wrap" style="max-width: 600px; padding: 60px 24px; text-align: center;">
          <div class="card" style="padding: 24px; background: var(--accent-soft); border-color: var(--accent);">
            <p class="t-body" style="margin: 0 0 16px;">No grant selected.</p>
            <a class="btn btn-outline-primary" data-nav="admin-dashboard">← Back to dashboard</a>
          </div>
        </main>
      `;
    }
    return html`
      <nav class="wrap" style="padding: 20px 40px 0; max-width: 1240px;">
        <div class="t-small" style="color: var(--ink-soft);">
          <a class="gf-breadcrumb" data-nav="admin-dashboard">Dashboard</a>
          <span style="margin: 0 8px; opacity: 0.5;">/</span>
          <span style="color: var(--ink-2);">Grant review</span>
        </div>
      </nav>
      <main class="wrap" style="max-width: 1240px; padding: 20px 24px 96px;">
        <div id="gr-root">
          <p class="t-body" style="padding: 60px 0; text-align: center;">Loading grant…</p>
        </div>
        ${{ __raw: approveModal() + denyModal() + infoModal() + deleteModal() + previewModal() }}
      </main>
    `;
  },

  mount(root: HTMLElement, ctx: PageContext): void {
    const idRaw = ctx.params.get('id');
    const idNum = idRaw == null ? NaN : Number(idRaw);
    const id: number | null = Number.isFinite(idNum) ? idNum : null;
    if (id == null) return;

    let alive = true;
    let activeTab: TabId = 'overview';
    let isInternal = false;
    let actionLoading = false;
    let previewUrl: string | null = null;
    let msgDraft = '';

    let grant: Grant | null = null;
    let documents: DocRow[] = [];
    let messages:  Message[] = [];
    let history:   HistoryRow[] = [];
    let staff:     StaffMember[] = [];

    const rootEl = root.querySelector('#gr-root') as HTMLElement | null;

    // ── Bootstrap modal helpers ──────────────────────────────────
    const Bs = (): { Modal: { getOrCreateInstance(el: HTMLElement): { show(): void; hide(): void } } } | undefined =>
      (window as unknown as { bootstrap?: { Modal: { getOrCreateInstance(el: HTMLElement): { show(): void; hide(): void } } } }).bootstrap;
    function showModal(sel: string): void {
      const el = root.querySelector(sel) as HTMLElement | null;
      if (!el) return; Bs()?.Modal.getOrCreateInstance(el).show();
    }
    function hideModal(sel: string): void {
      const el = root.querySelector(sel) as HTMLElement | null;
      if (!el) return; Bs()?.Modal.getOrCreateInstance(el).hide();
    }

    // PDF-preview blob cleanup on close
    const previewEl = root.querySelector('#grPreviewModal') as HTMLElement | null;
    const frameEl   = root.querySelector('#gr-preview-frame') as HTMLIFrameElement | null;
    if (previewEl) {
      previewEl.addEventListener('hidden.bs.modal', () => {
        if (frameEl) frameEl.src = 'about:blank';
        if (previewUrl) { URL.revokeObjectURL(previewUrl); previewUrl = null; }
      });
    }

    // ── Renderers ────────────────────────────────────────────────
    function renderHeader(g: Grant): string {
      const daysElapsed = daysBetween(g.SubmittedAt);
      return html`
        <div class="card" style="margin-bottom: 24px; padding: 24px 28px;">
          <div class="d-flex" style="justify-content: space-between; align-items: flex-start;
                                     flex-wrap: wrap; gap: 16px;">
            <div>
              <h1 class="t-h2 serif" style="margin: 0; font-size: 30px;">${g.ProjectName}</h1>
              <div class="d-flex" style="gap: 12px; align-items: center; flex-wrap: wrap; margin-top: 12px;">
                <span class="pill ${statusPillClass(g.Status)}">${g.Status}</span>
                ${{ __raw: g.ReferenceNumber ? html`
                  <span class="t-small">Ref: <strong style="color: var(--ink);">#${g.ReferenceNumber}</strong></span>
                ` : '' }}
                ${{ __raw: g.SubmittedAt ? html`
                  <span style="color: var(--rule-2);">·</span>
                  <span class="t-small">Submitted ${fmtDate(g.SubmittedAt)}</span>
                  <span style="color: var(--rule-2);">·</span>
                  <span style="font-size: 13px; color: var(--st-denied); font-weight: 500;">
                    ${daysElapsed} day${daysElapsed === 1 ? '' : 's'} elapsed
                  </span>
                ` : '' }}
              </div>
              <p class="t-small" style="margin-top: 8px;">
                ${g.OrganizationLegalName || ''} · EIN: ${g.TaxId || '\u2014'}
              </p>
            </div>
            <div style="text-align: right;">
              <div class="serif" style="font-size: 36px; color: var(--primary); line-height: 1;">
                ${fmtMoneyOrDash(g.RequestedAmount)}
              </div>
              <p class="t-small" style="margin: 4px 0 0;">Requested amount</p>
            </div>
          </div>
        </div>
      `;
    }

    function renderTabBar(): string {
      const tabs = TABS.map(t => {
        const active = activeTab === t.id;
        const badge = t.id === 'communication' && messages.length > 0
          ? `<span style="margin-left: 6px; background: var(--st-denied); color: var(--paper);
                          border-radius: 99px; padding: 1px 6px; font-size: 11px; font-weight: 600;">${messages.length}</span>`
          : '';
        return `
          <button type="button" data-action="set-tab" data-tab="${t.id}"
                  style="padding: 10px 18px; font-size: 14px; font-weight: 500; border: none;
                         cursor: pointer; background: transparent;
                         color: ${active ? 'var(--primary)' : 'var(--ink-soft)'};
                         border-bottom: 2px solid ${active ? 'var(--primary)' : 'transparent'};
                         margin-bottom: -1px;">${esc(t.label)}${badge}</button>
        `;
      }).join('');
      return `<div class="d-flex" style="gap: 8px; border-bottom: 1px solid var(--rule);
                                          margin-bottom: 16px; flex-wrap: wrap;">${tabs}</div>`;
    }

    function overviewTab(g: Grant): string {
      const yes501 = g.TaxExemptStatus === 'Yes';
      return html`
        <div class="card" style="padding: 24px;">
          <h3 class="t-h4 serif" style="margin: 0 0 16px; font-size: 20px;">Proposal narrative</h3>
          <div class="label">Project title</div>
          <p style="font-weight: 500; margin: 6px 0 16px;">${g.ProjectName}</p>
          ${{ __raw: g.ProjectDescription ? html`
            <div class="label">Brief description</div>
            <p class="t-body" style="margin: 8px 0 16px; line-height: 1.75; white-space: pre-wrap;">${g.ProjectDescription}</p>
          ` : '' }}
          ${{ __raw: g.ImpactNarrative ? html`
            <div class="label">Impact narrative</div>
            <p class="t-body" style="margin: 8px 0 16px; line-height: 1.75; white-space: pre-wrap;">${g.ImpactNarrative}</p>
          ` : '' }}
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px;
                      background: var(--paper-2); border-radius: 10px; padding: 18px; margin-top: 12px;">
            <div>
              <div class="label">501(c)(3) status</div>
              ${{ __raw: yes501
                ? html`<div class="d-flex" style="gap: 8px; align-items: center; margin-top: 8px;">
                    <span style="color: var(--primary); font-size: 18px;">✓</span>
                    <span style="font-weight: 500; color: var(--primary);">Verified 501(c)(3)</span>
                  </div>`
                : html`<p class="t-body" style="margin: 6px 0 0;">${g.TaxExemptStatus || '\u2014'}</p>` }}
            </div>
            <div>
              <div class="label">Timeline</div>
              <p class="t-body" style="margin: 6px 0 0;">
                ${g.ProjectStartDate ? fmtDate(g.ProjectStartDate) : '\u2014'}
                ${{ __raw: ' &rarr; ' }}
                ${g.ProjectEndDate ? fmtDate(g.ProjectEndDate) : '\u2014'}
              </p>
            </div>
          </div>
        </div>
      `;
    }

    function documentsTab(): string {
      if (documents.length === 0) {
        return `
          <div class="card" style="padding: 24px;">
            <h3 class="t-h4 serif" style="margin: 0 0 16px; font-size: 20px;">Supporting documents</h3>
            <p class="t-body">No documents uploaded.</p>
          </div>
        `;
      }
      const rows = documents.map(doc => {
        const ext = (doc.FileExtension || '').replace('.', '').toUpperCase();
        const isLink = /^https?:\/\//i.test(doc.FilePath || '');
        const isPdf = ext === 'PDF';
        const required = doc.DocumentType === 'IRS Letter' || doc.DocumentType === 'Proposal';
        const requiredPill = required ? `
          <span style="font-size: 11.5px; font-weight: 600; color: var(--st-approved);
                background: var(--primary-soft); padding: 2px 8px; border-radius: 999px;">Required</span>` : '';
        const actions = isLink
          ? `<a href="${esc(doc.FilePath || '')}" target="_blank" rel="noopener noreferrer"
                class="btn btn-outline-primary btn-sm">Open link →</a>`
          : `
            <div class="d-flex" style="gap: 6px;">
              ${isPdf ? `<button type="button" class="btn btn-text btn-sm"
                                  data-action="preview-doc" data-doc-id="${doc.DocumentId}">Preview</button>` : ''}
              <button type="button" class="btn btn-outline-primary btn-sm"
                      data-action="download-doc" data-doc-id="${doc.DocumentId}"
                      data-doc-name="${esc(doc.FileName)}">Download</button>
            </div>
          `;
        return `
          <div class="d-flex" style="justify-content: space-between; align-items: center;
                                     padding: 14px 0; border-bottom: 1px solid var(--rule); gap: 16px;">
            <div style="min-width: 0;">
              <div style="font-weight: 500; font-size: 14px; word-break: break-word;">${esc(doc.FileName)}</div>
              <div class="t-small">${esc(doc.DocumentType)} · ${esc(ext)} · ${esc(fmtFileSize(doc.FileSize))} · ${esc(fmtDate(doc.UploadedAt))}</div>
            </div>
            <div class="d-flex" style="gap: 8px; align-items: center;">
              ${requiredPill}${actions}
            </div>
          </div>
        `;
      }).join('');
      return `
        <div class="card" style="padding: 24px;">
          <h3 class="t-h4 serif" style="margin: 0 0 16px; font-size: 20px;">Supporting documents</h3>
          ${rows}
          <p class="t-small" style="margin-top: 16px;">${documents.length} file${documents.length === 1 ? '' : 's'}</p>
        </div>
      `;
    }

    function organizationTab(g: Grant): string {
      const verifiedPill = g.TaxExemptStatus === 'Yes'
        ? `<span style="padding: 3px 10px; border-radius: 999px; font-size: 11.5px; font-weight: 600;
            background: var(--primary-soft); color: var(--st-approved); text-transform: uppercase;
            letter-spacing: 0.04em;">Verified 501(c)(3)</span>` : '';
      const orgRows: Array<[string, string]> = [
        ['Legal name',   g.OrganizationLegalName || '\u2014'],
        ['Tax ID / EIN', g.TaxId || '\u2014'],
      ];
      const contactRows: Array<[string, string]> = [
        ['Contact', [g.ContactFirstName, g.ContactLastName].filter(Boolean).join(' ') || '\u2014'],
        ['Email',   g.ContactEmail || '\u2014'],
        ['Phone',   g.ContactPhone || '\u2014'],
        ['Address', [g.AddressStreet, g.AddressCity, g.AddressState, g.AddressZip].filter(Boolean).join(', ') || '\u2014'],
      ];
      const orgCell = (k: string, v: string) => `
        <div>
          <div class="label">${esc(k)}</div>
          <div style="font-weight: 500; margin-top: 4px;">${esc(v)}</div>
        </div>
      `;
      return `
        <div class="card" style="padding: 24px;">
          <div class="d-flex" style="justify-content: space-between; align-items: center; margin-bottom: 16px;">
            <h3 class="t-h4 serif" style="margin: 0; font-size: 20px;">Organization details</h3>
            ${verifiedPill}
          </div>
          <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px;
                      background: var(--paper-2); border-radius: 10px; padding: 16px; margin-bottom: 20px;">
            ${orgRows.map(([k, v]) => orgCell(k, v)).join('')}
          </div>
          <h4 class="t-h4 serif" style="margin: 0 0 12px; font-size: 16px;">Contact information</h4>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
            ${contactRows.map(([k, v]) => orgCell(k, v)).join('')}
          </div>
        </div>
      `;
    }

    function communicationTab(): string {
      const msgList = messages.length === 0
        ? '<p class="t-body">No messages yet.</p>'
        : messages.map(m => {
            const internal = !!m.IsInternal;
            const internalChip = internal
              ? `<div style="font-size: 11.5px; font-weight: 600; color: var(--accent);
                  margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.06em;">🔒 Internal — not visible to applicant</div>`
              : '';
            return `
              <div style="background: ${internal ? 'var(--accent-soft)' : 'var(--card)'};
                          border: 1px solid ${internal ? 'var(--accent)' : 'var(--rule)'};
                          border-radius: 10px; padding: 12px 14px;">
                ${internalChip}
                <div class="d-flex" style="gap: 8px; align-items: center; margin-bottom: 4px; flex-wrap: wrap;">
                  <span style="font-weight: 500; font-size: 13.5px;">${esc(m.SenderName)}</span>
                  ${roleBadge(m.SenderRole)}
                  <span class="t-small">${esc(fmtDate(m.SentAt))}</span>
                </div>
                <p class="t-body" style="margin: 0; font-size: 14px; white-space: pre-wrap;">${esc(m.MessageText)}</p>
              </div>
            `;
          }).join('');

      const chipBtn = (active: boolean, label: string, action: string, color: 'primary' | 'accent') => `
        <button type="button" data-action="${action}"
                style="padding: 6px 14px; border-radius: 999px; font-weight: 500; font-size: 13px;
                       border: none; cursor: pointer;
                       background: ${active ? `var(--${color})` : 'var(--paper-2)'};
                       color: ${active ? 'var(--paper)' : 'var(--ink-2)'};">${label}</button>
      `;

      const composerBg = isInternal ? 'var(--accent-soft)' : 'var(--paper-2)';
      const composerBanner = isInternal
        ? '<p style="font-size: 12.5px; color: var(--accent); font-weight: 500; margin: 0 0 10px;">🔒 This note will only be visible to approvers and admins.</p>'
        : '';
      const sendLabel = isInternal ? 'Save internal note' : 'Send message';
      const sendClass = isInternal ? 'btn btn-warning' : 'btn btn-primary';
      const disabled = (!msgDraft.trim() || actionLoading) ? ' disabled' : '';

      return html`
        <div class="card" style="padding: 24px;">
          <h3 class="t-h4 serif" style="margin: 0 0 16px; font-size: 20px;">Communication thread</h3>
          <div class="d-flex" style="gap: 8px; flex-wrap: wrap; margin-bottom: 24px;">
            ${{ __raw:
              chipBtn(!isInternal, 'Message applicant', 'set-msg-external', 'primary') +
              chipBtn( isInternal, '🔒 Internal note',  'set-msg-internal', 'accent')
            }}
          </div>
          <div style="display: flex; flex-direction: column; gap: 14px; margin-bottom: 24px;">
            ${{ __raw: msgList }}
          </div>
          <div class="card" style="background: ${{ __raw: composerBg }}; padding: 16px;">
            ${{ __raw: composerBanner }}
            <div class="gf-field" style="margin-bottom: 16px;">
              <textarea id="gr-msg-input" class="form-control" rows="4"
                        placeholder="${isInternal ? 'Write an internal note…' : 'Write a message to the applicant…'}"
                        style="resize: vertical;">${esc(msgDraft)}</textarea>
            </div>
            <button type="button" class="${sendClass}" data-action="send-message"${{ __raw: disabled }}>
              ${sendLabel}
            </button>
          </div>
        </div>
      `;
    }

    function tabBody(g: Grant): string {
      if (activeTab === 'overview')      return overviewTab(g);
      if (activeTab === 'documents')     return documentsTab();
      if (activeTab === 'organization')  return organizationTab(g);
      if (activeTab === 'communication') return communicationTab();
      return '';
    }

    function sidebar(g: Grant): string {
      const canAct = ['Submitted', 'Under Review', 'Action Required'].includes(g.Status);
      const isTerminal = g.Status === 'Approved' || g.Status === 'Denied';
      const isDraft = g.Status === 'Draft';
      const isAdmin = ctx.userRole === 'admin';

      const startBtn = g.Status === 'Submitted'
        ? `<button type="button" class="btn btn-primary" style="width: 100%; margin-bottom: 10px;"
                   data-action="start-review"${actionLoading ? ' disabled' : ''}>Start review</button>`
        : '';

      let actionBlock = '';
      if (isDraft) {
        actionBlock = `
          <p class="t-small" style="margin: 0 0 12px; line-height: 1.5;">
            This grant is still a <strong style="color: var(--ink);">Draft</strong> — the applicant
            hasn't submitted it yet, so there's nothing to approve or deny.
          </p>
          ${isAdmin ? `
            <button type="button" class="btn btn-danger" style="width: 100%; margin-bottom: 16px;"
                    data-action="open-delete"${actionLoading ? ' disabled' : ''}>Delete draft</button>
          ` : ''}
        `;
      } else if (!isTerminal) {
        actionBlock = `
          <div style="display: flex; flex-direction: column; gap: 8px; margin-bottom: 16px;">
            <button type="button" class="btn btn-primary"        style="width: 100%;"
                    data-action="open-approve"${(!canAct || actionLoading) ? ' disabled' : ''}>Approve grant</button>
            <button type="button" class="btn btn-danger"         style="width: 100%;"
                    data-action="open-deny"   ${(!canAct || actionLoading) ? ' disabled' : ''}>Deny grant</button>
            <button type="button" class="btn btn-outline-primary" style="width: 100%;"
                    data-action="open-info"   ${(!canAct || actionLoading) ? ' disabled' : ''}>Request more info</button>
          </div>
        `;
      }

      const staffOpts = ['<option value="">Unassigned</option>'].concat(
        staff.map(s => `<option value="${s.UserId}"${g.ReviewedBy === s.UserId ? ' selected' : ''}>${esc(s.FullName)} (${esc(s.Role)})</option>`)
      ).join('');

      const publicCard = g.Status === 'Approved' ? `
        <div class="card" style="padding: 24px; margin-bottom: 16px;">
          <h4 class="t-h4 serif" style="margin: 0 0 10px; font-size: 17px;">Public showcase</h4>
          <label style="display: flex; gap: 10px; align-items: center; margin-bottom: 8px; cursor: pointer;">
            <input type="checkbox" data-action="set-public" data-field="IsPublic"${g.IsPublic ? ' checked' : ''}
                   style="accent-color: var(--primary);"/>
            <span style="font-size: 14px;">Public</span>
          </label>
          <label style="display: flex; gap: 10px; align-items: center; cursor: pointer;">
            <input type="checkbox" data-action="set-public" data-field="IsAmountPublic"${g.IsAmountPublic ? ' checked' : ''}
                   style="accent-color: var(--primary);"/>
            <span style="font-size: 14px;">Show amount</span>
          </label>
        </div>` : '';

      return `
        <div style="position: sticky; top: 80px;">
          <div class="card" style="padding: 24px; margin-bottom: 16px;">
            <h4 class="t-h4 serif" style="margin: 0 0 14px; font-size: 17px;">Review actions</h4>
            ${startBtn}
            ${actionBlock}
            <div style="border-top: 1px solid var(--rule); padding-top: 12px;">
              <label class="label" style="display: block; margin-bottom: 6px;">Assigned to</label>
              <select data-action="assign-reviewer" class="form-select"
                      ${actionLoading ? 'disabled' : ''}>${staffOpts}</select>
            </div>
          </div>
          ${publicCard}
          <div class="card" style="padding: 24px;">
            <h4 class="t-h4 serif" style="margin: 0 0 14px; font-size: 17px;">Status history</h4>
            ${renderTimeline(history)}
          </div>
        </div>
      `;
    }

    function repaint(): void {
      if (!rootEl) return;
      if (!grant) {
        rootEl.innerHTML = `
          <div class="card" style="padding: 24px; background: var(--accent-soft); border-color: var(--accent);">
            <p class="t-body" style="margin: 0 0 16px;">Grant not found.</p>
            <a class="btn btn-outline-primary" data-nav="admin-dashboard">← Back to dashboard</a>
          </div>`;
        return;
      }
      const grid = `
        <div style="display: grid; grid-template-columns: 1fr 320px; gap: 24px; align-items: start;">
          <div>${renderTabBar()}${tabBody(grant)}</div>
          ${sidebar(grant)}
        </div>
      `;
      rootEl.innerHTML = renderHeader(grant) + grid;

      // Restore composer focus to end if we just repainted while typing.
      if (activeTab === 'communication') {
        const ta = rootEl.querySelector<HTMLTextAreaElement>('#gr-msg-input');
        if (ta) {
          ta.value = msgDraft;
          ta.setSelectionRange(msgDraft.length, msgDraft.length);
        }
      }
    }

    // ── Actions ──────────────────────────────────────────────────
    async function refreshGrantAndHistory(): Promise<void> {
      if (id == null) return;
      try {
        const [g, h] = await Promise.all([window.api.getGrant(id), window.api.getHistory(id)]);
        if (!alive) return;
        grant = g as Grant; history = (h as HistoryRow[]) || [];
        repaint();
      } catch (e) { console.error(e); }
    }

    async function startReview(): Promise<void> {
      if (id == null) return; actionLoading = true; repaint();
      try { await window.api.reviewGrant(id); await refreshGrantAndHistory(); }
      catch (e) { alert((e as Error).message); }
      finally { actionLoading = false; if (alive) repaint(); }
    }

    async function assignReviewer(val: string): Promise<void> {
      if (id == null) return;
      try {
        await window.api.assignGrant(id, val === '' ? null : Number(val));
        await refreshGrantAndHistory();
      } catch (e) { alert((e as Error).message); }
    }

    async function setPublic(field: 'IsPublic' | 'IsAmountPublic', value: boolean): Promise<void> {
      if (id == null) return;
      try {
        const updated = await window.api.setPublic(id, { [field]: value });
        if (!alive) return; grant = updated as Grant; repaint();
      } catch (e) { alert((e as Error).message); }
    }

    function openApprove(): void {
      const amountInput = root.querySelector<HTMLInputElement>('#gr-approve-amount');
      const publicChk   = root.querySelector<HTMLInputElement>('#gr-approve-public');
      const errEl       = root.querySelector<HTMLElement>('#gr-approve-error');
      if (amountInput && grant) amountInput.value = grant.RequestedAmount != null ? String(grant.RequestedAmount) : '';
      if (publicChk) publicChk.checked = false;
      if (errEl)    { errEl.style.display = 'none'; errEl.textContent = ''; }
      showModal('#grApproveModal');
    }

    async function confirmApprove(): Promise<void> {
      if (id == null) return;
      const amountInput = root.querySelector<HTMLInputElement>('#gr-approve-amount');
      const publicChk   = root.querySelector<HTMLInputElement>('#gr-approve-public');
      const errEl       = root.querySelector<HTMLElement>('#gr-approve-error');
      const raw = amountInput?.value || '';
      const amt = Number(raw);
      if (!raw || isNaN(amt) || amt <= 0) {
        if (errEl) { errEl.style.display = 'block'; errEl.textContent = 'Enter a valid amount greater than 0.'; }
        return;
      }
      actionLoading = true;
      try {
        await window.api.approveGrant(id, {
          ApprovedAmount: amt,
          IsPublic: !!publicChk?.checked,
          IsAmountPublic: true,
        });
        hideModal('#grApproveModal');
        await refreshGrantAndHistory();
      } catch (e) { alert((e as Error).message); }
      finally { actionLoading = false; if (alive) repaint(); }
    }

    function openDeny(): void {
      const tplSel = root.querySelector<HTMLSelectElement>('#gr-deny-template');
      const ta     = root.querySelector<HTMLTextAreaElement>('#gr-deny-reason');
      const notify = root.querySelector<HTMLInputElement>('#gr-deny-notify');
      const errEl  = root.querySelector<HTMLElement>('#gr-deny-error');
      if (tplSel) tplSel.value = '';
      if (ta) ta.value = '';
      if (notify) notify.checked = true;
      if (errEl) { errEl.style.display = 'none'; errEl.textContent = ''; }
      updateDenyCounter();
      showModal('#grDenyModal');
    }

    function updateDenyCounter(): void {
      const ta      = root.querySelector<HTMLTextAreaElement>('#gr-deny-reason');
      const counter = root.querySelector<HTMLElement>('#gr-deny-counter');
      const btn     = root.querySelector<HTMLButtonElement>('[data-action="confirm-deny"]');
      const len = (ta?.value || '').trim().length;
      if (counter) {
        counter.textContent = `${len} / 50 characters minimum`;
        counter.style.color = len < 50 ? 'var(--st-denied)' : 'var(--ink-soft)';
      }
      if (btn) btn.disabled = actionLoading || len < 50;
    }

    async function confirmDeny(): Promise<void> {
      if (id == null) return;
      const ta     = root.querySelector<HTMLTextAreaElement>('#gr-deny-reason');
      const notify = root.querySelector<HTMLInputElement>('#gr-deny-notify');
      const errEl  = root.querySelector<HTMLElement>('#gr-deny-error');
      const reason = (ta?.value || '').trim();
      if (reason.length < 50) {
        if (errEl) { errEl.style.display = 'block'; errEl.textContent = 'Reason must be at least 50 characters.'; }
        return;
      }
      actionLoading = true;
      try {
        await window.api.denyGrant(id, reason, !!notify?.checked);
        hideModal('#grDenyModal');
        await refreshGrantAndHistory();
      } catch (e) { alert((e as Error).message); }
      finally { actionLoading = false; if (alive) repaint(); }
    }

    function openInfo(): void {
      const ta = root.querySelector<HTMLTextAreaElement>('#gr-info-msg');
      if (ta) ta.value = '';
      const btn = root.querySelector<HTMLButtonElement>('[data-action="confirm-info"]');
      if (btn) btn.disabled = true;
      showModal('#grInfoModal');
    }

    function openDelete(): void {
      const errEl = root.querySelector<HTMLElement>('#gr-delete-error');
      if (errEl) { errEl.style.display = 'none'; errEl.textContent = ''; }
      showModal('#grDeleteModal');
    }

    async function confirmDelete(): Promise<void> {
      if (id == null) return;
      const errEl = root.querySelector<HTMLElement>('#gr-delete-error');
      actionLoading = true;
      try {
        await window.api.deleteGrant(id);
        hideModal('#grDeleteModal');
        ctx.navigate('admin-dashboard');
      } catch (e) {
        if (errEl) { errEl.style.display = 'block'; errEl.textContent = (e as Error).message; }
      } finally { actionLoading = false; if (alive) repaint(); }
    }

    async function confirmInfo(): Promise<void> {
      if (id == null) return;
      const ta = root.querySelector<HTMLTextAreaElement>('#gr-info-msg');
      const msg = (ta?.value || '').trim();
      if (!msg) return;
      actionLoading = true;
      try {
        await window.api.requestChanges(id, msg);
        hideModal('#grInfoModal');
        const msgs = await window.api.listMessages(id) as Message[];
        if (alive) { messages = msgs || []; }
        await refreshGrantAndHistory();
      } catch (e) { alert((e as Error).message); }
      finally { actionLoading = false; if (alive) repaint(); }
    }

    async function sendMessage(): Promise<void> {
      if (id == null) return;
      const ta = root.querySelector<HTMLTextAreaElement>('#gr-msg-input');
      const text = (ta?.value || '').trim();
      if (!text) return;
      msgDraft = text;
      try {
        await window.api.postMessage(id, text, isInternal);
        const msgs = await window.api.listMessages(id) as Message[];
        if (!alive) return;
        messages = msgs || [];
        msgDraft = '';
        repaint();
      } catch (e) { alert((e as Error).message); }
    }

    async function previewDoc(docId: number): Promise<void> {
      if (id == null) return;
      try {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        previewUrl = await window.api.getDocumentBlob(id, docId);
        if (!alive) return;
        if (frameEl) frameEl.src = previewUrl;
        showModal('#grPreviewModal');
      } catch (e) { alert((e as Error).message); }
    }

    async function downloadDoc(docId: number, fileName: string): Promise<void> {
      if (id == null) return;
      try { await window.api.downloadDocument(id, docId, fileName); }
      catch (e) { alert((e as Error).message); }
    }

    // ── Event delegation ─────────────────────────────────────────
    root.addEventListener('click', (e) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      const actionEl = t.closest('[data-action]') as HTMLElement | null;
      if (!actionEl) return;
      const action = actionEl.getAttribute('data-action');
      switch (action) {
        case 'set-tab': {
          const tab = actionEl.getAttribute('data-tab') as TabId | null;
          if (tab) { activeTab = tab; repaint(); }
          break;
        }
        case 'set-msg-external':  isInternal = false; repaint(); break;
        case 'set-msg-internal':  isInternal = true;  repaint(); break;
        case 'send-message':      void sendMessage(); break;
        case 'preview-doc': {
          const did = Number(actionEl.getAttribute('data-doc-id')); if (did) void previewDoc(did);
          break;
        }
        case 'download-doc': {
          const did = Number(actionEl.getAttribute('data-doc-id'));
          const nm  = actionEl.getAttribute('data-doc-name') || '';
          if (did) void downloadDoc(did, nm);
          break;
        }
        case 'start-review':      void startReview(); break;
        case 'open-approve':      openApprove(); break;
        case 'confirm-approve':   void confirmApprove(); break;
        case 'open-deny':         openDeny(); break;
        case 'confirm-deny':      void confirmDeny(); break;
        case 'open-info':         openInfo(); break;
        case 'confirm-info':      void confirmInfo(); break;
        case 'open-delete':       openDelete(); break;
        case 'confirm-delete':    void confirmDelete(); break;
      }
    });

    root.addEventListener('change', (e) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      const tplSel = t.closest('#gr-deny-template') as HTMLSelectElement | null;
      if (tplSel) {
        const tpl = DENY_TEMPLATES[tplSel.value] || '';
        const ta  = root.querySelector<HTMLTextAreaElement>('#gr-deny-reason');
        if (ta) { ta.value = tpl; updateDenyCounter(); }
        return;
      }
      const assignSel = t.closest('[data-action="assign-reviewer"]') as HTMLSelectElement | null;
      if (assignSel) { void assignReviewer(assignSel.value); return; }
      const pubChk = t.closest('[data-action="set-public"]') as HTMLInputElement | null;
      if (pubChk) {
        const field = pubChk.getAttribute('data-field') as 'IsPublic' | 'IsAmountPublic' | null;
        if (field) void setPublic(field, pubChk.checked);
      }
    });

    root.addEventListener('input', (e) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      if (t.closest('#gr-deny-reason')) { updateDenyCounter(); return; }
      const infoTa = t.closest('#gr-info-msg') as HTMLTextAreaElement | null;
      if (infoTa) {
        const btn = root.querySelector<HTMLButtonElement>('[data-action="confirm-info"]');
        if (btn) btn.disabled = !infoTa.value.trim() || actionLoading;
        return;
      }
      const msgTa = t.closest('#gr-msg-input') as HTMLTextAreaElement | null;
      if (msgTa) {
        msgDraft = msgTa.value;
        const btn = root.querySelector<HTMLButtonElement>('[data-action="send-message"]');
        if (btn) btn.disabled = !msgDraft.trim() || actionLoading;
      }
    });

    // ── Fetch ────────────────────────────────────────────────────
    Promise.all([
      window.api.getGrant(id),
      window.api.listDocuments(id),
      window.api.listMessages(id),
      window.api.getHistory(id),
      window.api.listStaff().catch(() => []),
    ]).then(([g, d, m, h, s]) => {
      if (!alive) return;
      grant     = g as Grant;
      documents = (d as DocRow[]) || [];
      messages  = (m as Message[]) || [];
      history   = (h as HistoryRow[]) || [];
      staff     = (s as StaffMember[]) || [];
      repaint();
    }).catch(err => {
      if (!alive) return;
      if (rootEl) {
        rootEl.innerHTML = `
          <div class="card" style="padding: 24px; background: var(--accent-soft); border-color: var(--accent);">
            <p class="t-body" style="margin: 0 0 16px;">Could not load grant (${esc((err as Error).message || 'network')}).</p>
            <a class="btn btn-outline-primary" data-nav="admin-dashboard">← Back to dashboard</a>
          </div>`;
      }
    });

    (GrantReviewPage as Page & { _alive?: () => void })._alive = () => {
      alive = false;
      if (previewUrl) { URL.revokeObjectURL(previewUrl); previewUrl = null; }
    };
  },

  unmount(): void {
    const stop = (GrantReviewPage as Page & { _alive?: () => void })._alive;
    if (stop) stop();
  },
};

// fmtMoney is imported for parity with grant-detail; unused directly here.
void fmtMoney;

export default GrantReviewPage;
